import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { GoogleReviewData, GoogleReviewModel } from 'constants/dataTypes';
import notionStoreDataService from 'services/notion.stores.service';
import { Review } from './Review';
import WriteReviewPopup from './WriteReviewPopup';

const StoreGooglePageReviews = ({ reviews, storeInfo }: { reviews: GoogleReviewModel, storeInfo: any }) => {

    const router = useRouter();
    const [stores, setStores] = useState([]);
    useEffect(() => {
        const fetchStoresData = async () => {
            try {
                setLoading(true);
                const storeData = await notionStoreDataService.getStoresDropdownData(router.locale || 'au');
                setStores(storeData);
                setLoading(false);
            } catch (error) {
                console.error('Failed to fetch store data:', error);
            }
        };

        fetchStoresData();
    }, [router.locale]);

    const [displayedReviews, setDisplayedReviews] = useState<GoogleReviewData[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const reviewsPerPage = 5;
    const totalPages = reviews?.data ? Math.ceil(
        (Array.isArray(reviews.data) ? reviews.data.length : Object.keys(reviews.data).length) / reviewsPerPage
    ) : 0;
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (reviews?.data) {
            const startIndex = (currentPage - 1) * reviewsPerPage;
            const endIndex = startIndex + reviewsPerPage;
            const reviewsArray = Array.isArray(reviews.data) 
                ? reviews.data 
                : Object.values(reviews.data);
            setDisplayedReviews(reviewsArray.slice(startIndex, endIndex) as GoogleReviewData[]);
        }
    }, [reviews, currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    if (!reviews) {
        return null;
    }
    return (
        <section className="bg-white py-[40px] px-4 sm:pt-[48px] sm:pb-[78px]">

            <div className="mx-auto lg:max-w-[1244px]">
                <span className="mb-[22px] block text-center text-[24px] font-normal text-brand-black100 sm:mb-[10px] md:text-[36px]">
                    Recent Google reviews from <br></br>  <span className='font-bold'>{storeInfo.name}</span>
                </span>
                <div className="mb-6 flex items-center justify-center">
                    <div className="flex items-baseline">
                        <span className="mr-1 text-lg font-semibold">{reviews?.rating.toFixed(1)}</span>
                        <div className="mr-2 flex items-center">
                            <span className="text-[#FBBB06] text-xl">★★★★★</span>
                        </div>
                        <span className="mr-1 text-lg font-semibold">{reviews?.review_count}</span>
                        <span className="text-[#959595] text-lg">reviews</span>
                    </div>
                </div>
                <div className="mb-6 flex items-center justify-center">
                    <button className="mb-6 rounded-full bg-[#2A2A2A] px-12 py-3 text-sm font-medium text-white" onClick={() => {
                        window.open(`${storeInfo.google_review_link}`, '_blank');
                    }}>
                        Write a review
                    </button>
                </div>
                <div className="relative mx-auto space-y-4 sm:px-1 lg:max-w-[700px] shop:max-w-[980px] xl:max-w-[1100px] 2xl:max-w-none">
                    {displayedReviews.length > 0 ? (
                        <>
                            {displayedReviews.map((review: GoogleReviewData, index: number) => (
                                <Review key={index} {...review} />
                            ))}

                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-8">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="text-[#959595] disabled:opacity-50"
                                    >
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M11.3 12.0008L15.2 8.10078C15.3833 7.91745 15.475 7.68411 15.475 7.40078C15.475 7.11745 15.3833 6.88411 15.2 6.70078C15.0167 6.51745 14.7833 6.42578 14.5 6.42578C14.2167 6.42578 13.9833 6.51745 13.8 6.70078L9.2 11.3008C9.1 11.4008 9.02917 11.5091 8.9875 11.6258C8.94583 11.7424 8.925 11.8674 8.925 12.0008C8.925 12.1341 8.94583 12.2591 8.9875 12.3758C9.02917 12.4924 9.1 12.6008 9.2 12.7008L13.8 17.3008C13.9833 17.4841 14.2167 17.5758 14.5 17.5758C14.7833 17.5758 15.0167 17.4841 15.2 17.3008C15.3833 17.1174 15.475 16.8841 15.475 16.6008C15.475 16.3174 15.3833 16.0841 15.2 15.9008L11.3 12.0008Z" fill="#00AEC7" />
                                        </svg>
                                    </button>

                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNumber = index + 1;
                                        // Show current page, and up to 5 surrounding pages (total of 6 pages)
                                        const shouldShowPage =
                                            pageNumber === 1 || // Always show first page
                                            (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 3) || // Show 2 pages before and 3 after current
                                            (currentPage <= 3 && pageNumber <= 6); // Show first 6 pages when at start

                                        if (!shouldShowPage) {
                                            if (pageNumber === currentPage - 3 || pageNumber === currentPage + 4) {
                                                return <span key={pageNumber} className="text-[#959595]">...</span>;
                                            }
                                            return null;
                                        }

                                        return (
                                            <button
                                                key={pageNumber}
                                                onClick={() => handlePageChange(pageNumber)}
                                                className={`w-8 h-8 rounded-full ${currentPage === pageNumber
                                                    ? 'bg-[#00AEC7] text-white'
                                                    : 'text-[#959595]'
                                                    }`}
                                            >
                                                {pageNumber}
                                            </button>
                                        );
                                    })}

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="text-[#959595] disabled:opacity-50"
                                    >
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M13.1 12.0008L9.2 8.10078C9.01667 7.91745 8.925 7.68411 8.925 7.40078C8.925 7.11745 9.01667 6.88411 9.2 6.70078C9.38334 6.51745 9.61667 6.42578 9.9 6.42578C10.1833 6.42578 10.4167 6.51745 10.6 6.70078L15.2 11.3008C15.3 11.4008 15.3708 11.5091 15.4125 11.6258C15.4542 11.7424 15.475 11.8674 15.475 12.0008C15.475 12.1341 15.4542 12.2591 15.4125 12.3758C15.3708 12.4924 15.3 12.6008 15.2 12.7008L10.6 17.3008C10.4167 17.4841 10.1833 17.5758 9.9 17.5758C9.61667 17.5758 9.38334 17.4841 9.2 17.3008C9.01667 17.1174 8.925 16.8841 8.925 16.6008C8.925 16.3174 9.01667 16.0841 9.2 15.9008L13.1 12.0008Z" fill="#00AEC7" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center text-gray-500">No reviews available</div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default StoreGooglePageReviews;
